import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription, interval } from 'rxjs';
import { PageHeroComponent } from '../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { PermissionService } from '../security/permission.service';
import { app_permissions } from '../security/app-permissions';
import { WASenderAccount, WASenderAttachment, WASenderConversation, WASenderMessage } from './wasender.model';
import { WASenderService } from './wasender.service';

@Component({
  selector: 'app-wasender-chat',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbModule,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TagModule,
    TextareaModule,
    ToastModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent
  ],
  providers: [MessageService],
  templateUrl: './wasender-chat.component.html',
  styleUrl: './wasender-chat.component.css'
})
export class WASenderChatComponent implements OnInit, OnDestroy {
  private readonly wasenderService = inject(WASenderService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  @ViewChild('messageScroll') messageScroll?: ElementRef<HTMLDivElement>;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  breadcrumbItems: MenuItem[] = [
    { label: 'Workspace' },
    { label: 'WhatsApp Chat', routerLink: '/wasender/chat' }
  ];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  accounts: WASenderAccount[] = [];
  conversations: WASenderConversation[] = [];
  messages: WASenderMessage[] = [];
  selectedAccountId?: number;
  selectedConversation?: WASenderConversation;
  conversationSearch = '';
  composerText = '';
  selectedFiles: File[] = [];
  newRecipientDialogVisible = false;
  newRecipientPhone = '';
  newRecipientName = '';
  newRecipientMessage = '';
  newRecipientAccountId?: number;
  loadingAccounts = false;
  loadingConversations = false;
  loadingMessages = false;
  sending = false;
  canSend = false;

  private pollSubscription?: Subscription;

  ngOnInit(): void {
    this.canSend = this.permissionService.hasPermission(app_permissions.wasender.chat.send);
    this.loadAccounts();
    this.loadConversations();
    this.pollSubscription = interval(10000).subscribe(() => this.refreshQuietly());
  }

  ngOnDestroy(): void {
    this.pollSubscription?.unsubscribe();
  }

  loadAccounts(): void {
    this.loadingAccounts = true;
    this.wasenderService.getAccounts().subscribe({
      next: accounts => {
        this.accounts = accounts.filter(account => account.active !== false);
        this.newRecipientAccountId = this.accounts.find(account => account.defaultAccount)?.id || this.accounts[0]?.id;
        this.loadingAccounts = false;
      },
      error: () => {
        this.accounts = [];
        this.loadingAccounts = false;
      }
    });
  }

  loadConversations(keepSelection = true): void {
    this.loadingConversations = true;
    const selectedId = this.selectedConversation?.id;
    this.wasenderService.getConversations(this.selectedAccountId, this.conversationSearch).subscribe({
      next: conversations => {
        this.conversations = conversations;
        this.loadingConversations = false;
        if (keepSelection && selectedId) {
          this.selectedConversation = conversations.find(conversation => conversation.id === selectedId);
        }
        if (!this.selectedConversation && conversations.length) {
          this.selectConversation(conversations[0]);
        }
      },
      error: () => {
        this.conversations = [];
        this.loadingConversations = false;
      }
    });
  }

  selectConversation(conversation: WASenderConversation): void {
    this.selectedConversation = conversation;
    this.loadMessages(conversation.id);
  }

  loadMessages(conversationId: number): void {
    this.loadingMessages = true;
    this.wasenderService.getMessages(conversationId).subscribe({
      next: messages => {
        this.messages = messages;
        this.loadingMessages = false;
        setTimeout(() => this.scrollToBottom(), 0);
      },
      error: () => {
        this.messages = [];
        this.loadingMessages = false;
      }
    });
  }

  refreshQuietly(): void {
    this.wasenderService.getConversations(this.selectedAccountId, this.conversationSearch).subscribe({
      next: conversations => {
        const selectedId = this.selectedConversation?.id;
        this.conversations = conversations;
        if (selectedId) {
          this.selectedConversation = conversations.find(conversation => conversation.id === selectedId) || this.selectedConversation;
          this.wasenderService.getMessages(selectedId).subscribe({
            next: messages => {
              this.messages = messages;
              setTimeout(() => this.scrollToBottom(), 0);
            },
            error: () => undefined
          });
        }
      },
      error: () => undefined
    });
  }

  onAccountFilterChange(): void {
    this.selectedConversation = undefined;
    this.messages = [];
    this.loadConversations(false);
  }

  clearSearch(): void {
    this.conversationSearch = '';
    this.loadConversations(false);
  }

  openNewRecipient(): void {
    this.newRecipientPhone = '';
    this.newRecipientName = '';
    this.newRecipientMessage = '';
    this.newRecipientAccountId = this.selectedAccountId || this.accounts.find(account => account.defaultAccount)?.id || this.accounts[0]?.id;
    this.newRecipientDialogVisible = true;
  }

  sendNewRecipientMessage(): void {
    const phone = this.newRecipientPhone.trim();
    const text = this.newRecipientMessage.trim();
    if (!phone || !text || !this.canSend) {
      return;
    }
    this.sending = true;
    this.wasenderService.sendMessage({
      accountId: this.newRecipientAccountId,
      to: phone,
      contactName: this.newRecipientName.trim() || undefined,
      text
    }).subscribe({
      next: response => {
        this.sending = false;
        this.newRecipientDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Message Queued', detail: 'The message will be sent through Cloud Tasks.' });
        this.loadConversations(false);
        setTimeout(() => {
          const conversation = this.conversations.find(item => item.id === response.conversationId);
          if (conversation) this.selectConversation(conversation);
        }, 300);
      },
      error: () => {
        this.sending = false;
        this.messageService.add({ severity: 'error', summary: 'Send Failed', detail: 'Unable to queue this message.' });
      }
    });
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFiles = Array.from(input.files || []);
  }

  removeSelectedFile(index: number): void {
    this.selectedFiles = this.selectedFiles.filter((_, currentIndex) => currentIndex !== index);
    if (!this.selectedFiles.length && this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  sendComposerMessage(): void {
    const conversation = this.selectedConversation;
    const text = this.composerText.trim();
    if (!conversation || !this.canSend || this.sending || (!text && !this.selectedFiles.length)) {
      return;
    }
    this.sending = true;
    const request = {
      accountId: conversation.accountId,
      conversationId: conversation.id,
      to: conversation.contactPhoneNumber || conversation.contactJid,
      contactName: conversation.contactName,
      text
    };
    const sender = this.selectedFiles.length
      ? this.wasenderService.sendMultipartMessage(request, this.selectedFiles)
      : this.wasenderService.sendMessage(request);

    sender.subscribe({
      next: () => {
        this.sending = false;
        this.composerText = '';
        this.selectedFiles = [];
        if (this.fileInput?.nativeElement) this.fileInput.nativeElement.value = '';
        this.loadConversations(true);
        this.loadMessages(conversation.id);
        this.messageService.add({ severity: 'success', summary: 'Message Queued', detail: 'Queued for rate-controlled sending.' });
      },
      error: () => {
        this.sending = false;
        this.messageService.add({ severity: 'error', summary: 'Send Failed', detail: 'Unable to queue this message.' });
      }
    });
  }

  conversationTitle(conversation: WASenderConversation): string {
    return conversation.contactName || conversation.contactPhoneNumber || conversation.contactJid || 'Unknown contact';
  }

  conversationSubtitle(conversation: WASenderConversation): string {
    return [conversation.contactPhoneNumber, conversation.accountDisplayName].filter(Boolean).join(' · ') || '-';
  }

  messageTime(message: WASenderMessage): string | Date | undefined {
    return message.receivedAt || message.sentAt || message.scheduledAt || message.createdAt;
  }

  messageStatusSeverity(status?: string): any {
    switch (status) {
      case 'SENT':
      case 'RECEIVED':
        return 'success';
      case 'FAILED':
        return 'danger';
      case 'SENDING':
        return 'info';
      default:
        return 'warn';
    }
  }

  primaryAttachment(message: WASenderMessage): WASenderAttachment | undefined {
    return message.attachments?.[0];
  }

  isImage(attachment?: WASenderAttachment): boolean {
    return !!attachment?.mimeType?.startsWith('image/') || attachment?.mediaKind === 'IMAGE';
  }

  attachmentIcon(attachment?: WASenderAttachment): string {
    if (!attachment) return 'pi pi-paperclip';
    if (attachment.mediaKind === 'IMAGE') return 'pi pi-image';
    if (attachment.mediaKind === 'VIDEO') return 'pi pi-video';
    if (attachment.mediaKind === 'AUDIO') return 'pi pi-volume-up';
    return 'pi pi-file';
  }

  fileSizeLabel(size?: number): string {
    if (!size) return '';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / 1024 / 1024).toFixed(1)} MB`;
  }

  scrollToBottom(): void {
    const element = this.messageScroll?.nativeElement;
    if (element) {
      element.scrollTop = element.scrollHeight;
    }
  }
}
